import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Alert,
  InputAdornment,
  IconButton,
  Fade,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
} from "@mui/icons-material";
import { getUsers, login } from "../api/api";
import "./style.css";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleLogin = async () => {
    setError("");
    try {
      const res = await login({ username, password });
      localStorage.setItem("isAuthenticated", "true");
      if (res.user) {
        localStorage.setItem("user", JSON.stringify(res.user));
      }
      navigate("/chat");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Login failed, please try again";
      setError(errorMessage);
      console.error("Login error:", errorMessage);
    }
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const textFieldStyle = {
    color: '#111827',
    '& .MuiOutlinedInput-notchedOutline': { 
      borderColor: 'rgba(0,0,0,0.1)',
      borderRadius: '16px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0,0,0,0.3)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#111827',
      boxShadow: '0 0 15px rgba(0,0,0,0.05)'
    },
    '& .MuiInputBase-input': {
      padding: '16px 20px'
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        py: 4,
        fontFamily: "'Inter', sans-serif"
      }}
    >
      <Container maxWidth="sm" sx={{ zIndex: 50 }}>
        <Paper
          elevation={0}
          className="fade-in"
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: '24px',
            background: "rgba(255, 255, 255, 0.7)",
            backdropFilter: "blur(24px)",
            boxShadow: "0 16px 40px -10px rgba(0, 0, 0, 0.08)",
            border: "1px solid rgba(0, 0, 0, 0.04)",
            color: "#111827"
          }}
        >
          <Box sx={{ mb: 5, textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: "'Anton', sans-serif",
                fontWeight: 400,
                mb: 1,
                letterSpacing: "-0.02em",
                color: "#111827",
                textTransform: 'uppercase'
              }}
            >
              LOGIN
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, color: '#4B5563', fontSize: '1rem' }}>
              Welcome back
            </Typography>
          </Box>

          {error && (
            <Fade in>
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  borderRadius: 3, 
                  bgcolor: 'rgba(239, 68, 68, 0.1)', 
                  color: '#B91C1C',
                  border: '1px solid rgba(239, 68, 68, 0.2)'
                }}
              >
                {error}
              </Alert>
            </Fade>
          )}

          <Box component="form" noValidate>
            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              variant="outlined"
              required
              margin="normal"
              InputProps={{
                sx: textFieldStyle
              }}
              InputLabelProps={{ sx: { color: '#6B7280', ml: 1 } }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              required
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClickShowPassword} edge="end" sx={{ color: '#6B7280' }}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: textFieldStyle
              }}
              InputLabelProps={{ sx: { color: '#6B7280', ml: 1 } }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleLogin}
              disabled={!username || !password}
              startIcon={<LoginIcon />}
              sx={{
                mt: 4,
                mb: 3,
                py: 2,
                fontSize: "1.1rem",
                fontWeight: 700,
                borderRadius: 4,
                textTransform: "uppercase",
                fontFamily: "'Inter', sans-serif",
                background: (username && password) ? "#111827" : "rgba(0,0,0,0.05)",
                color: (username && password) ? "white" : "rgba(0,0,0,0.3)",
                boxShadow: (username && password) ? "0 10px 20px rgba(0, 0, 0, 0.1)" : "none",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "#000",
                  transform: "translateY(-2px)",
                  boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)",
                },
                "&:disabled": { 
                  color: "rgba(0,0,0,0.3)", 
                  background: "rgba(0,0,0,0.05)" 
                }
              }}
            >
              Login
            </Button>
            <Box textAlign="center">
              <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  style={{
                    color: "#111827",
                    fontWeight: 700,
                    textDecoration: "none",
                  }}
                >
                  Sign Up
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Login;
